import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSurveys } from '../hooks/useSurveys';

const Home: React.FC = () => {
  const [qrCode, setQrCode] = useState('');
  const navigate = useNavigate();
  const { getSurveyByAccessCode } = useSurveys();

  const handleQrSubmit = async () => {
    const code = qrCode.trim();
    if (!code) {
      alert('Please enter a code');
      return;
    }

    // Check if it's a 5-digit access code
    if (/^\d{5}$/.test(code)) {
      try {
        const survey = await getSurveyByAccessCode(code);
        navigate(`/survey/${survey.id}`);
      } catch (error) {
        alert('Invalid access code. Please check and try again.');
      }
    } else {
      navigate(`/survey/qr/${code}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQrSubmit();
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: 'var(--color-primary)' }}
    >
      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)'
        }}></div>
      </div>

      {/* Centered Content */}
      <div className="relative z-10 flex flex-col items-center px-4 w-full max-w-md">
        {/* Logo */}
        <img
          src="/cc8.png"
          alt="Crisci Create Logo"
          className="h-20 w-auto mb-6"
        />

        {/* Title */}
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
          color: 'var(--color-accent)',
          lineHeight: 1.2,
          textAlign: 'center',
          marginBottom: '0.5rem'
        }}>
          Crisci Create
        </h1>

        {/* Subtitle */}
        <p className="text-base mb-10 text-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
          Pharmacy Research Platform
        </p>

        {/* Survey Access Card */}
        <div
          className="w-full rounded-2xl p-8"
          style={{
            backgroundColor: 'white',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
            borderTop: '4px solid var(--color-accent)'
          }}
        >
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.5rem',
            color: 'var(--color-primary)',
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            Access Your Survey
          </h2>
          <p className="text-sm text-center mb-6" style={{ color: 'var(--color-muted)' }}>
            Enter your 5-digit code to begin
          </p>

          <div className="space-y-4">
            <input
              type="text"
              value={qrCode}
              onChange={(e) => setQrCode(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder="12345"
              className="glass-input w-full text-center text-2xl font-mono font-bold tracking-wider"
              style={{ padding: '1rem 1.5rem' }}
              maxLength={10}
            />
            <button
              onClick={handleQrSubmit}
              className="glass-btn-accent glass-btn-full glass-btn-lg flex items-center justify-center gap-2"
            >
              <span>Start Survey</span>
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Staff Sign In */}
        <Link
          to="/admin"
          className="mt-6 text-sm transition-colors"
          style={{ color: 'rgba(255,255,255,0.5)' }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        >
          Staff Sign In →
        </Link>
      </div>

      {/* Tiny Footer */}
      <div className="absolute bottom-4 text-center">
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
          © 2026 Crisci Group
        </p>
      </div>
    </div>
  );
};

export default Home;
